import React, { createContext, useState, useContext, useEffect } from 'react';
import { firestore } from 'config/firebase';
import { doc, setDoc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { useAuthContext } from './AuthContext';

const WishlistContext = createContext();

export const useWishlistContext = () => useContext(WishlistContext);

export const WishlistProvider = ({ children }) => {
    const { user, isAuthenticated } = useAuthContext();
    const [wishlistItems, setWishlistItems] = useState([]);
    const MAX_WISHLIST_ITEMS = 10;

    // Get user wishlist from Firestore
    const fetchWishlist = async (userId) => {
        const wishlistDoc = await getDoc(doc(firestore, "wishlists", userId));
        if (wishlistDoc.exists()) {
            const items = wishlistDoc.data().items || [];
            setWishlistItems(items.slice(0, MAX_WISHLIST_ITEMS));
        } else {
            setWishlistItems([]);
        }
    };

    // Add item to wishlist
    const addToWishlist = async (product) => {
        if (wishlistItems.length >= MAX_WISHLIST_ITEMS) {
            console.warn("Wishlist is full. Cannot add more items.");
            return;
        }
        if (!wishlistItems.find(item => item.id === product.id)) {
            const updatedWishlist = [...wishlistItems, product].slice(0, MAX_WISHLIST_ITEMS);
            setWishlistItems(updatedWishlist);
            if (user) {
                await setDoc(doc(firestore, "wishlists", user.uid), { items: updatedWishlist });
            }
        }
    };

    // Remove item from wishlist
    const removeFromWishlist = async (productId) => {
        const updatedWishlist = wishlistItems.filter(item => item.id !== productId);
        setWishlistItems(updatedWishlist);
        if (user) {
            await updateDoc(doc(firestore, "wishlists", user.uid), { items: updatedWishlist });
        }
    };

    // Clear the wishlist
    const clearWishlist = async () => {
        setWishlistItems([]);
        if (user) {
            await deleteDoc(doc(firestore, "wishlists", user.uid));
        }
    };

    useEffect(() => {
        if (isAuthenticated && user && user.uid) {
            fetchWishlist(user.uid);
        } else {
            setWishlistItems([]);
        }
    }, [isAuthenticated, user]);

    return (
        <WishlistContext.Provider value={{ wishlistItems, addToWishlist, removeFromWishlist, clearWishlist }}>
            {children}
        </WishlistContext.Provider>
    );
};